/**
 * vehicle-finder-facets.js
 * Drop-in replacement:
 * - When a Make is selected, we load Model/Year/Category facet values from the make-specific collection
 *   (e.g. /collections/bmw) to avoid Shopify's global facet value truncation on /collections/all.
 * - Still supports the original base collection URL for makes list + fallback behavior.
 * - Pass 212: exact vehicle_fitments drive Model/Year whenever the selected model is migrated,
 *   while unmigrated models keep the legacy Model/Year facets. Make collections also lock the
 *   Make selector to the matching collection handle.
 */
(function () {
  const DEFAULT_SECTION_IDS = ["main-collection-product-grid", "main-collection", "collection-main"];

  function enc(v) {
    return encodeURIComponent(String(v ?? "").trim());
  }

  function buildUrl(base, params) {
    const parts = [];
    Object.entries(params).forEach(([key, rawValue]) => {
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      values.forEach((value) => {
        if (String(value || "").trim() === "") return;
        parts.push(`${enc(key)}=${enc(value)}`);
      });
    });
    if (!parts.length) return base;
    return `${base}${base.includes("?") ? "&" : "?"}${parts.join("&")}`;
  }

  async function fetchCollectionHtml(url, forceFull = false) {
    if (!forceFull) {
      for (const id of DEFAULT_SECTION_IDS) {
        try {
          const u = url + (url.includes("?") ? "&" : "?") + `section_id=${encodeURIComponent(id)}`;
          const res = await fetch(u, { credentials: "same-origin" });
          if (!res.ok) continue;
          const html = await res.text();
          if (html && html.length > 100) return html;
        } catch (_) {}
      }
    }

    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch collection HTML");
    return await res.text();
  }

  function extractFilterValues(html, key) {
    if (!key) return [];
    const doc = new DOMParser().parseFromString(html, "text/html");

    const inputs = Array.from(doc.querySelectorAll(`input[name="${CSS.escape(key)}"]`));
    const fromInputs = inputs.map((i) => i.value).filter(Boolean);

    const selects = Array.from(doc.querySelectorAll(`select[name="${CSS.escape(key)}"]`));
    const fromSelects = selects.flatMap((s) =>
      Array.from(s.querySelectorAll("option"))
        .filter((o) => o.value)
        .map((o) => o.value)
    );

    const links = Array.from(doc.querySelectorAll(`a[href*="${encodeURIComponent(key)}="]`));
    const fromLinks = links
      .map((a) => {
        try {
          const u = new URL(a.getAttribute("href"), window.location.origin);
          return u.searchParams.get(key);
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    const uniq = Array.from(new Set([...fromInputs, ...fromSelects, ...fromLinks]));

    const allYears = uniq.length > 0 && uniq.every((v) => /^\d{4}$/.test(v));
    if (allYears) uniq.sort((a, b) => Number(a) - Number(b));
    else uniq.sort((a, b) => a.localeCompare(b));

    return uniq;
  }

  function setSelectOptions(selectEl, values, placeholder, preferredValue = null) {
    if (!selectEl) return;

    const keep = preferredValue ?? selectEl.value;

    selectEl.innerHTML = "";
    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = placeholder;
    selectEl.appendChild(ph);

    values.forEach((v) => {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      selectEl.appendChild(opt);
    });

    if (keep && values.includes(keep)) selectEl.value = keep;
  }

  // IMPORTANT: do NOT wipe options during loading (that was causing resets)
  function setLoading(selectEl, loadingText) {
    if (!selectEl) return;
    selectEl.disabled = true;

    // update placeholder text if present, without destroying current options/value
    const ph = selectEl.querySelector('option[value=""]');
    if (ph) ph.textContent = loadingText;
  }

  function clearAndDisable(selectEl, placeholder) {
    if (!selectEl) return;
    selectEl.disabled = true;
    setSelectOptions(selectEl, [], placeholder, null);
    selectEl.value = "";
  }

  // --- Make -> Collection handle helpers (NEW) ---
  function slugifyHandle(str) {
    return String(str || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  const collectionExistsCache = new Map();

  async function collectionExists(path) {
    if (collectionExistsCache.has(path)) return await collectionExistsCache.get(path);

    const check = (async () => {
      try {
        const res = await fetch(path, { method: "HEAD", credentials: "same-origin" });
        return res.ok;
      } catch {
        return false;
      }
    })();

    // Cache the in-flight Promise too, so Year + Category refreshes cannot
    // issue duplicate HEAD requests for the same make at the same time.
    collectionExistsCache.set(path, check);
    const exists = await check;
    collectionExistsCache.set(path, exists);
    return exists;
  }

  const exactFitmentIndexCache = new Map();

  function normaliseExact(value) {
    return String(value || "").trim().toLowerCase();
  }

  async function fetchExactFitmentIndex(url) {
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.delete("page");
    sectionUrl.searchParams.delete("section_id");
    sectionUrl.searchParams.delete("sections");

    const cacheKey = sectionUrl.href;
    const cached = exactFitmentIndexCache.get(cacheKey);
    if (cached && Date.now() - cached.time < 60000) return { ...cached.value, fitments: cached.value.fitments.map((item) => ({ ...item })) };

    const products = new Map();
    const legacyModels = new Map();
    const legacyYears = new Map();
    const fitments = [];
    const nativeValues = new Map();
    const nativeLabels = new Map();
    const seen = new Set();
    let fitmentParam = "";
    let filterEnabled = false;
    let complete = false;

    for (let page = 0; page < 100; page += 1) {
      if (seen.has(sectionUrl.href)) throw new Error("Repeated exact-fitment results page.");
      seen.add(sectionUrl.href);

      const response = await fetch(sectionUrl.href, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Exact vehicle fitments could not be loaded.");
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const index = doc.querySelector("[data-rmx-fitment-index]");
      if (!index) throw new Error("Exact vehicle fitment response was not available.");

      if (!fitmentParam) fitmentParam = String(index.dataset.fitmentParam || "").trim();
      filterEnabled = filterEnabled || index.dataset.fitmentFilterEnabled === "true";

      index.querySelectorAll("[data-native-fitment-value]").forEach((item) => {
        const value = String(item.dataset.nativeFitmentValue || "").trim();
        const label = String(item.dataset.nativeFitmentLabel || "").trim();
        if (value) nativeValues.set(value, value);
        if (value && label) nativeLabels.set(normaliseExact(label), value);
      });

      index.querySelectorAll("[data-fitment-product]").forEach((item) => {
        const id = String(item.dataset.fitmentProduct || "").trim();
        if (!id) return;
        const hasExact = item.dataset.hasExactFitments === "true";
        products.set(id, (products.get(id) || false) || hasExact);
      });

      index.querySelectorAll("[data-fitment-legacy-model]").forEach((item) => {
        const productId = String(item.dataset.productId || "").trim();
        const model = String(item.dataset.model || "").trim();
        if (!productId || !model) return;
        if (!legacyModels.has(productId)) legacyModels.set(productId, new Set());
        legacyModels.get(productId).add(model);
      });

      index.querySelectorAll("[data-fitment-legacy-year]").forEach((item) => {
        const productId = String(item.dataset.productId || "").trim();
        const year = String(item.dataset.year || "").trim();
        if (!productId || !/^\d{4}$/.test(year)) return;
        if (!legacyYears.has(productId)) legacyYears.set(productId, new Set());
        legacyYears.get(productId).add(year);
      });

      index.querySelectorAll("[data-fitment-item]").forEach((item) => {
        const id = String(item.dataset.fitmentId || "").trim();
        const label = String(item.dataset.fitmentLabel || "").trim();
        if (!id && !label) return;
        fitments.push({
          productId: String(item.dataset.productId || "").trim(),
          id,
          handle: String(item.dataset.fitmentHandle || "").trim(),
          label,
          make: String(item.dataset.fitmentMake || "").trim(),
          model: String(item.dataset.fitmentModel || "").trim(),
          chassis: String(item.dataset.fitmentChassis || "").trim(),
          year: String(item.dataset.fitmentYear || "").trim(),
          note: String(item.dataset.fitmentNote || "").trim()
        });
      });

      if (!index.dataset.nextUrl) {
        complete = true;
        break;
      }

      const next = new URL(index.dataset.nextUrl, window.location.href);
      const nextPage = next.searchParams.get("page");
      if (!nextPage) throw new Error("Unexpected exact-fitment pagination.");
      sectionUrl.searchParams.set("page", nextPage);
    }

    if (!complete) throw new Error("Exact vehicle fitment list is too large to refresh completely.");

    fitments.forEach((item) => {
      item.filterValue = nativeValues.get(item.id) || nativeLabels.get(normaliseExact(item.label)) || item.id;
    });

    const allProductsExact = products.size > 0 && Array.from(products.values()).every(Boolean);
    const value = {
      fitmentParam,
      filterEnabled,
      fitments,
      legacyModels,
      legacyYears,
      products,
      allProductsExact,
      productCount: products.size
    };

    if (exactFitmentIndexCache.size >= 24) exactFitmentIndexCache.delete(exactFitmentIndexCache.keys().next().value);
    exactFitmentIndexCache.set(cacheKey, { time: Date.now(), value });
    return { ...value, fitments: fitments.map((item) => ({ ...item })) };
  }

  async function initFinder(root) {
    if (!root || root.dataset.vehicleFinderReady === "true") return;
    root.dataset.vehicleFinderReady = "true";

    const base = root.dataset.collectionUrl || "/collections/all";

    const keyMake = root.dataset.keyMake || "";
    const keyModel = root.dataset.keyModel || "";
    const keyYear = root.dataset.keyYear || ""; // optional
    const keyType = root.dataset.keyType || ""; // optional

    const makeSel = root.querySelector(".vehicle-finder__make");
    const modelSel = root.querySelector(".vehicle-finder__model");
    const yearSel = root.querySelector(".vehicle-finder__year");
    const typeSel = root.querySelector(".vehicle-finder__type");
    const btn = root.querySelector(".vehicle-finder__submit");
    const status = root.querySelector(".vehicle-finder__status");
    const submitMode = root.dataset.submitMode || "navigate";
    const requireType = root.dataset.requireType === "true";
    const autoSubmit = root.dataset.autoSubmit === "true";
    const emptyTypeLabel = root.dataset.emptyTypeLabel || "";

    if (!makeSel || !modelSel || !btn) return;

    let suppress = false;
    let autoTimer = null;
    let exactYearState = null;
    let exactYearRequest = 0;

    function setStatus(message) {
      if (status) status.textContent = message || "";
    }

    function updateButtonState() {
      const hasRequiredType = !requireType || (typeSel && typeSel.value);
      btn.disabled = !(makeSel.value && modelSel.value && hasRequiredType);
    }

    function currentParams(includeYear = true, includeType = true) {
      const params = { [keyMake]: makeSel.value, [keyModel]: modelSel.value };
      if (includeYear && yearSel && keyYear && yearSel.value) params[keyYear] = yearSel.value;
      if (includeType && typeSel && keyType && typeSel.value) params[keyType] = typeSel.value;
      return params;
    }

    // NEW: choose the most reliable base for the selected make
    // - Prefer /collections/<make-handle> if it exists
    // - Else fall back to original base (usually /collections/all)
    async function baseForMake(make) {
      if (!make) return base;

      const handle = slugifyHandle(make);
      const makeCollection = `/collections/${handle}`;

      if (await collectionExists(makeCollection)) return makeCollection;
      return base;
    }

    async function refreshSelectPreserve({ selectEl, key, placeholder, url }) {
      if (!selectEl || !key) return { kept: true };

      const keep = selectEl.value; // remember BEFORE fetch/rebuild

      let html = await fetchCollectionHtml(url);
      let values = extractFilterValues(html, key);
      if (!values.length) {
        html = await fetchCollectionHtml(url, true);
        values = extractFilterValues(html, key);
      }

      suppress = true;
      setSelectOptions(selectEl, values, placeholder, keep);
      selectEl.disabled = values.length === 0;

      // If the kept value is NOT in the new list, clear it
      if (keep && !values.includes(keep)) selectEl.value = "";

      suppress = false;
      return { kept: !keep || values.includes(keep) };
    }

    function exactFitmentsForSelection(state, year, requireNativeFilter = false) {
      if (!state) return [];
      if (requireNativeFilter && (!state.fitmentParam || !state.filterEnabled)) return [];
      const make = normaliseExact(makeSel.value);
      const model = normaliseExact(modelSel.value);
      const selectedYear = String(year || "").trim();
      return state.fitments.filter((item) => {
        if (normaliseExact(item.make) !== make || normaliseExact(item.model) !== model) return false;
        if (selectedYear && String(item.year) !== selectedYear) return false;
        return Boolean(item.filterValue);
      });
    }

    function exactFilterParamForCurrentYear() {
      if (!yearSel || !yearSel.value || !exactYearState) return null;
      const matches = exactFitmentsForSelection(exactYearState, yearSel.value, true);
      if (!matches.length) return null;
      const values = Array.from(new Set(matches.map((item) => item.filterValue).filter(Boolean)));
      if (!values.length) return null;
      return { key: exactYearState.fitmentParam, values };
    }

    async function refreshExactYears() {
      if (!yearSel || !keyYear) return false;
      const make = makeSel.value;
      const model = modelSel.value;
      if (!make || !model) return false;

      const requestId = ++exactYearRequest;
      const scopedBase = await baseForMake(make);

      // Pass 211: exact fitment data must be loaded independently of the legacy
      // Model/Year facets. A multi-model product can match an old broad model/year
      // combination even when that exact vehicle does not exist. Keep Make and
      // Category, but resolve the selected Model locally against vehicle_fitments.
      const exactParams = currentParams(false, true);
      if (keyModel) delete exactParams[keyModel];
      const exactUrl = buildUrl(scopedBase, exactParams);

      try {
        const state = await fetchExactFitmentIndex(exactUrl);
        if (requestId !== exactYearRequest) return false;
        const matches = exactFitmentsForSelection(state, "");
        if (!matches.length) {
          exactYearState = null;
          return false;
        }

        // Pass 209: as soon as this Make + Model has exact metaobject coverage,
        // those records become the authoritative year list. For unmigrated
        // products we only add legacy years when that product has a single model;
        // multi-model legacy year lists are inherently ambiguous and caused the
        // F-Pace 2008-2010 false availability.
        const yearsSet = new Set(matches.map((item) => String(item.year || "").trim()).filter((year) => /^\d{4}$/.test(year)));
        const wantedModel = normaliseExact(model);
        state.products.forEach((hasExact, productId) => {
          if (hasExact) return;
          const models = state.legacyModels.get(productId);
          if (!models || models.size !== 1) return;
          const onlyModel = Array.from(models)[0];
          if (normaliseExact(onlyModel) !== wantedModel) return;
          (state.legacyYears.get(productId) || []).forEach((year) => {
            if (/^\d{4}$/.test(String(year))) yearsSet.add(String(year));
          });
        });

        const years = Array.from(yearsSet).sort((a, b) => Number(a) - Number(b));
        if (!years.length) {
          exactYearState = null;
          return false;
        }

        const keep = yearSel.value;
        exactYearState = state;
        suppress = true;
        setSelectOptions(yearSel, years, "Select year…", keep);
        yearSel.disabled = false;
        if (keep && !years.includes(keep)) yearSel.value = "";
        suppress = false;
        return true;
      } catch (error) {
        if (requestId === exactYearRequest) exactYearState = null;
        console.debug("Exact vehicle-year filtering is not available for this selection; using legacy facets.", error);
        return false;
      }
    }

    async function refreshYears() {
      if (!yearSel || !keyYear) return;
      const make = makeSel.value,
        model = modelSel.value;
      if (!make || !model) return;

      // Pass 209: exact vehicle_fitments become authoritative for a Make + Model
      // as soon as exact records exist for that selection. This prevents product-wide
      // legacy year lists from leaking years from a different model on the same product.
      if (await refreshExactYears()) return;

      exactYearState = null;
      const scopedBase = await baseForMake(make);

      // Years respect category if selected
      const url = buildUrl(scopedBase, currentParams(false, true));

      await refreshSelectPreserve({
        selectEl: yearSel,
        key: keyYear,
        placeholder: "Select year…",
        url,
      });
    }

    async function refreshTypes() {
      if (!typeSel || !keyType) return;
      const make = makeSel.value,
        model = modelSel.value;
      if (!make || !model) return;

      const scopedBase = await baseForMake(make);

      // Types respect year if selected. In an exact-fitment-complete scope,
      // also constrain this request with the exact metaobject reference so a
      // multi-model product cannot leak categories from another model/year.
      const typeParams = currentParams(true, false);
      const exactFilter = exactFilterParamForCurrentYear();
      if (exactFilter) typeParams[exactFilter.key] = exactFilter.values;
      const url = buildUrl(scopedBase, typeParams);

      await refreshSelectPreserve({
        selectEl: typeSel,
        key: keyType,
        placeholder: "Select category…",
        url,
      });

      if (typeSel.disabled && emptyTypeLabel) {
        suppress = true;
        setSelectOptions(typeSel, [emptyTypeLabel], "Select category…", "");
        typeSel.disabled = false;
        suppress = false;
      }
    }

    // Initial UI
    setLoading(makeSel, "Loading makes…");
    clearAndDisable(modelSel, "Select model…");
    if (yearSel) clearAndDisable(yearSel, "Select year…");
    if (typeSel) clearAndDisable(typeSel, "Select category…");
    btn.disabled = true;

    function currentCollectionHandle() {
      try {
        const match = window.location.pathname.match(/^\/collections\/([^/?#]+)/i);
        return match ? decodeURIComponent(match[1]).toLowerCase() : "";
      } catch (error) {
        return "";
      }
    }

    function makeFromCurrentCollection(makes) {
      const handle = currentCollectionHandle();
      if (!handle || handle === "all") return "";
      return makes.find((make) => slugifyHandle(make) === handle) || "";
    }

    async function loadMakes() {
      setStatus("Loading vehicle options…");

      // Normally respect the configured base collection. A make collection can
      // omit its Make facet because every product already shares that value, so
      // fall back to /collections/all only when needed to identify the make from
      // the current /collections/<make> handle.
      let html = await fetchCollectionHtml(base);
      let makes = extractFilterValues(html, keyMake);
      let lockedMake = makeFromCurrentCollection(makes);

      const handle = currentCollectionHandle();
      if (handle && handle !== "all" && !lockedMake) {
        try {
          const globalHtml = await fetchCollectionHtml("/collections/all");
          const globalMakes = extractFilterValues(globalHtml, keyMake);
          const globalLockedMake = makeFromCurrentCollection(globalMakes);
          if (globalLockedMake) {
            makes = globalMakes;
            lockedMake = globalLockedMake;
          }
        } catch (_) {}
      }

      suppress = true;
      setSelectOptions(makeSel, makes, "Select make…", lockedMake);
      if (lockedMake) {
        makeSel.value = lockedMake;
        makeSel.disabled = true;
        makeSel.dataset.lockedToCollection = "true";
        makeSel.title = `Make fixed by this ${lockedMake} collection`;
      } else {
        makeSel.disabled = false;
        delete makeSel.dataset.lockedToCollection;
        makeSel.removeAttribute("title");
      }
      suppress = false;

      if (lockedMake) {
        setLoading(modelSel, "Loading models…");
        await loadModels(lockedMake);
      }
      setStatus("");
      updateButtonState();
    }

    async function loadModels(make) {
      // Load from a make-specific collection when available, but ALWAYS apply the
      // selected make as a facet as well. This is essential when baseForMake()
      // falls back to /collections/all (e.g. makes without /collections/<handle>),
      // otherwise Shopify exposes the global model facet list.
      const scopedBase = await baseForMake(make);
      const modelUrl = buildUrl(scopedBase, { [keyMake]: make });

      let html = await fetchCollectionHtml(modelUrl);
      let models = extractFilterValues(html, keyModel);
      if (!models.length) {
        html = await fetchCollectionHtml(modelUrl, true);
        models = extractFilterValues(html, keyModel);
      }

      suppress = true;
      setSelectOptions(modelSel, models, "Select model…");
      modelSel.disabled = models.length === 0;
      suppress = false;
    }

    async function submitFinder() {
      updateButtonState();
      if (btn.disabled) return;

      const scopedBase = await baseForMake(makeSel.value);
      const submitParams = currentParams(true, true);
      const exactFilter = exactFilterParamForCurrentYear();
      if (exactFilter) submitParams[exactFilter.key] = exactFilter.values;
      const url = buildUrl(scopedBase, submitParams);
      const detail = {
        url,
        make: makeSel.value,
        model: modelSel.value,
        year: yearSel ? yearSel.value : "",
        type: typeSel ? typeSel.value : "",
        exactFitment: exactFilter ? exactFilter.values.join(",") : "",
        exactFitmentParam: exactFilter ? exactFilter.key : ""
      };

      if (submitMode === "event") {
        root.dispatchEvent(new CustomEvent("remanx:vehicle-finder-submit", {
          bubbles: true,
          detail
        }));
        return;
      }

      window.location.assign(url);
    }

    function scheduleAutoSubmit() {
      if (!autoSubmit) return;
      window.clearTimeout(autoTimer);
      updateButtonState();
      if (btn.disabled) return;
      autoTimer = window.setTimeout(() => {
        submitFinder().catch((error) => console.warn("Vehicle Finder submit failed:", error));
      }, 180);
    }

    makeSel.addEventListener("change", async () => {
      if (suppress) return;

      const make = makeSel.value;
      exactYearState = null;
      exactYearRequest += 1;

      setLoading(modelSel, make ? "Loading models…" : "Select model…");
      if (yearSel) clearAndDisable(yearSel, "Select year…");
      if (typeSel) clearAndDisable(typeSel, "Select category…");
      btn.disabled = true;

      if (!make) {
        clearAndDisable(modelSel, "Select model…");
        return;
      }

      await loadModels(make);
      updateButtonState();
    });

    modelSel.addEventListener("change", async () => {
      if (suppress) return;

      const make = makeSel.value;
      const model = modelSel.value;
      exactYearState = null;
      exactYearRequest += 1;

      if (yearSel) clearAndDisable(yearSel, "Select year…");
      if (typeSel) clearAndDisable(typeSel, "Select category…");
      updateButtonState();

      if (!make || !model) return;

      // Load both optional lists (unconstrained by each other initially)
      if (yearSel && keyYear) setLoading(yearSel, "Loading years…");
      if (typeSel && keyType) setLoading(typeSel, "Loading categories…");

      await Promise.all([refreshYears(), refreshTypes()]);
      updateButtonState();
    });

    // Category change: refresh years, but KEEP year if still valid
    if (typeSel) {
      typeSel.addEventListener("change", async () => {
        if (suppress) return;

        if (yearSel && keyYear) {
          setLoading(yearSel, "Loading years…");
          await refreshYears();
        }

        updateButtonState();
        scheduleAutoSubmit();
      });
    }

    // Year change: refresh categories, but KEEP category if still valid
    if (yearSel) {
      yearSel.addEventListener("change", async () => {
        if (suppress) return;

        if (typeSel && keyType) {
          setLoading(typeSel, "Loading categories…");
          await refreshTypes();
        }

        updateButtonState();
        scheduleAutoSubmit();
      });
    }

    btn.addEventListener("click", () => {
      submitFinder().catch((error) => console.warn("Vehicle Finder submit failed:", error));
    });

    await loadMakes();
  }

  function boot(scope) {
    (scope || document).querySelectorAll(".vehicle-finder").forEach((el) => {
      initFinder(el).catch((e) => {
        el.dataset.vehicleFinderReady = "false";
        console.warn("Vehicle Finder init failed:", e);
      });
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot(document));
  else boot(document);

  document.addEventListener("shopify:section:load", (event) => boot(event.target));
})();
